import {
  Coordinates,
  NearbyBranch,
  ShipTo,
  ShipToChange,
  Warehouse,
  WarehouseChangeValidationDto,
} from "../models/warehouse/types";
import axiosService from "./axiosService";
import axiosRESTService from "./axiosRESTService";
import { URLS } from "./constants";
import {
  Lookup,
  BaseResponse,
  GoogleAnalyticsSettings,
  EmptyBaseResponse,
} from "../models/common/types";
import VersionURL from "../utils/versionUrl";
import {
  AddProductsResponse,
  BrandColorMappingResponse,
  CartItemRequest,
  MiniShoppingCartModel,
  NearbyBranchesRequest,
  ProductAvailableQuantity,
  ProductDetails,
  ProductPrice,
  VariantDetails,
  WindowsAndPatioDoorsActivityRequest,
} from "../models/product/types";
import { CustomerUser, FeaturedCategory } from "../models/customer/types";
import {
  BranchPageInfo,
  OverallBranches,
  StateBranches,
} from "../models/builderIO/types";
import {
  OptionGroup,
  OrderItemResponse,
  ProductListResponse,
  TemplatesAndEstimatesResponse,
} from "../models/productList/types";
import ProductListEnumType from "../../enums/productListEnumType";
import {
  VirtualProductQuoteRequest,
  VirtualProductQuoteResponse,
} from "../models/virtualProduct/types";
import {
  CartResponse,
  CartSpecificationLine,
  RiserPan,
  ShareShoppingCartRequest,
} from "../models/cart/types";
import ItemSourceEnum from "@/enums/itemSourceEnum";
import {
  CheckoutCompletedResponse,
  CheckoutRequest,
  CheckoutResponse,
  PlaceOrderResponse,
} from "../models/checkout/types";
import {
  DQDashboardRequest,
  DQDashboardResponse,
  DQDashboardResponseBase,
} from "../models/dqDashboard/types";
import {
  OrderNotificationResponse,
  OrderNotificationSettingRequest,
} from "../models/orderNotification/types";
import { ExportTypeEnum } from "@/enums/exportTypeEnum";
import { AxiosResponse } from "axios";
import qs from "qs";
import fetchLookupOptionsPayload from "../interfaces/payload/FetchLookupOptionsPayload";
import OrderDeliverySchedulePayload from "../interfaces/payload/OrderDeliverySchedulePayload";
import OrderHistoryFilterPayload from "../interfaces/payload/OrderHistoryFilterPayload";
import OrderHistoryStatusCountPayload from "../interfaces/payload/OrderHistoryStatusCountPayload";
import SendOrderChangeRequestPayload from "../interfaces/payload/SendOrderChangeRequestPayload";
import { ApiLookupResponse } from "../interfaces/response/ApiLookupResponse";
import { DQDeliveryOrderResponse } from "../interfaces/response/DQDeliveryOrderResponse";
import { OrderDetailsResponse } from "../interfaces/response/OrderDetailsResponse";
import OrderHistoryResponse from "../interfaces/response/OrderHistoryResponse";
import OrderHistoryStatusCountResponse from "../interfaces/response/OrderHistoryStatusCountResponse";
import { OrderDataForPDF } from "../interfaces/response/OrderPdfDataResponse";
import {
  ContactUsDto,
  MarketingContactUsDto,
} from "../models/contact-us/types";
import {
  ApproveUserRequest,
  CustomerAccountRequest,
  CustomerAccountResponse,
  ManageUsersResponse,
  SecondaryUserRequest,
  UpdatePasswordRequest,
  UserProfileRequest,
  UserProfileResponse,
  UserSelectedShipToResponse,
  ValidateEmailRequest,
  ValidateEmailResponse,
} from "../models/accountProfile/types";
import QuickReturnPayload from "../interfaces/payload/QuickReturnPaylaod";
import { ReturnOrderResponse } from "../interfaces/response/ReturnOrderResponse";
import { ReturnOrderRequest } from "../interfaces/ReturnOrderRequest";
import { ReturnItemsDetailsResponse } from "../interfaces/response/ReturnItemsDetailsResponse";
import { OrderAdvanceReturnRequest } from "../interfaces/OrderAdvanceReturnRequest";
import { OrderAdvanceReturnResponse } from "../interfaces/response/OrderAdvanceReturnResponse";
import ContractorData from "../interfaces/response/ContractorResponse";
import LoyaltyRebatePayload from "../interfaces/payload/LoyaltyRebatePayload";
import LoyaltyVendorResponse from "../interfaces/response/LoyaltyVendorResponse";
import LoyaltyRebateItemResponse from "../interfaces/response/LoyaltyRebateItemResponse";
import RebateInvoiceItem from "../interfaces/response/LoyaltyRebateResponse";
import LoyaltyRebateInvoiceResponse from "../interfaces/response/LoyaltyRebateInvoiceResponse";
import { RewardsEnrollmentResponse } from "../interfaces/response/RewardsEnrollmentResponse";
import RewardsBalanceHistoryPayload from "../interfaces/payload/RewardsBalanceHistoryPayload";
import RewardsBalanceHistoryResponse from "../interfaces/response/RewardsBalanceHistoryResponse";
import { RewardsEnrollmentStatusResponse } from "../interfaces/response/RewardsEnrollmentStatusResponse";

class DataService {
  async getAllBranchesOrderedByLocation(
    coordinates: Coordinates
  ): Promise<BaseResponse<Warehouse[]>> {
    const result = await axiosService.get<BaseResponse<Warehouse[]>>(
      VersionURL(URLS.GET_ALL_BRANCHES_ORDERED_BY_LOCATION_URL),
      {
        params: {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
        },
      }
    );

    return result.data;
  }

  async getAllBranches(
    warehouseCode: string,
    searchTerm: string | null
  ): Promise<BaseResponse<Warehouse[]>> {
    const result = await axiosService.get<BaseResponse<Warehouse[]>>(
      VersionURL(URLS.GET_ALL_BRANCHES),
      {
        params: {
          warehouseCode: warehouseCode,
          searchTerm: searchTerm,
        },
      }
    );

    return result.data;
  }

  async setSelectedWarehouse(
    warehouseCode: string
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosService.post<BaseResponse<boolean>>(
      VersionURL(URLS.SET_SELECTED_WAREHOUSE),
      null,
      {
        params: {
          warehouseCode: warehouseCode,
        },
      }
    );

    return result.data;
  }

  async validateWarehouseChange(
    warehouseCode: string
  ): Promise<BaseResponse<WarehouseChangeValidationDto>> {
    const result = await axiosService.post<
      BaseResponse<WarehouseChangeValidationDto>
    >(VersionURL(URLS.VALIDATE_WAREHOUSE_CHANGE), null, {
      params: {
        warehouseCode: warehouseCode,
      },
    });

    return result.data;
  }

  async getSortedCategories(): Promise<BaseResponse<string[]>> {
    const result = await axiosService.get<BaseResponse<string[]>>(
      VersionURL(URLS.GET_SORTED_CATEGORIES)
    );

    return result.data;
  }

  async getProductDetails(
    id: string | null,
    warehouseCode: string | null,
    fullDetails: boolean = false,
    useCougarsPricing: boolean = false
  ): Promise<BaseResponse<ProductDetails>> {
    const result = await axiosRESTService.get<BaseResponse<ProductDetails>>(
      URLS.GET_PRODUCT_DETAILS + "/" + id,
      {
        params: {
          warehouseCode: warehouseCode,
          fullDetails,
          useCougarsPricing,
        },
      }
    );

    return result.data;
  }

  async GetNearByBranches(
    productId: string | number,
    request: NearbyBranchesRequest
  ): Promise<BaseResponse<NearbyBranch[]>> {
    const result = await axiosRESTService.post<BaseResponse<NearbyBranch[]>>(
      VersionURL(`products/${productId}/nearby-branches`),
      request
    );

    return result.data;
  }

  async getProductBrandColors(
    id: number | null
  ): Promise<BaseResponse<BrandColorMappingResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<BrandColorMappingResponse>
    >(VersionURL(`products/${id}/color-mapping`));

    return result.data;
  }

  async getVariantDetails(
    id: number,
    warehouseCode: string | null
  ): Promise<BaseResponse<VariantDetails>> {
    const result = await axiosRESTService.get<BaseResponse<VariantDetails>>(
      `products/${id}/${URLS.GET_VARIANT_DETAILS}`,
      {
        params: {
          warehouseCode: warehouseCode,
        },
      }
    );

    return result.data;
  }

  async fetchMinPrice(productId: number): Promise<BaseResponse<ProductPrice>> {
    const result = await axiosRESTService.get<BaseResponse<ProductPrice>>(
      `products/${productId}/price`,
      {
        params: { lowestPrice: true },
      }
    );

    return result.data;
  }

  async fetchPriceByUom(
    productId: number,
    uomId: number
  ): Promise<BaseResponse<ProductPrice>> {
    const result = await axiosRESTService.get<BaseResponse<ProductPrice>>(
      `products/${productId}/price`,
      {
        params: { uomId },
      }
    );

    return result.data;
  }

  async getProductAvailabilityQuantity(
    productId: number,
    uomId: number
  ): Promise<BaseResponse<ProductAvailableQuantity>> {
    const result = await axiosRESTService.get<
      BaseResponse<ProductAvailableQuantity>
    >(`products/${productId}/availability`, {
      params: { uomId },
    });

    return result.data;
  }

  async getBranchLookUp(
    search: string,
    page: number,
    pageSize: number
  ): Promise<BaseResponse<Lookup>> {
    const result = await axiosRESTService.get<BaseResponse<Lookup>>(
      VersionURL(`products/warehouses/lookup`),
      {
        params: {
          search,
          page: page,
          pageSize: pageSize,
        },
      }
    );

    return result.data;
  }

  async addProductsToCart(
    items: CartItemRequest[]
  ): Promise<BaseResponse<AddProductsResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<AddProductsResponse>
    >(`cart/items`, items);
    return result.data;
  }

  async fetchEstimatesAndTemplates(): Promise<OptionGroup[]> {
    const result = await axiosRESTService.get<{
      data: TemplatesAndEstimatesResponse;
    }>(VersionURL("templates-estimates"));

    const response = result.data.data;

    return [
      {
        label: "-Templates-",
        options: response.templates,
      },
      {
        label: "-Estimates-",
        options: response.estimates,
      },
    ];
  }

  async getQuotedOrders(): Promise<{ id: number; jobNo: string }[]> {
    const result = await axiosRESTService.get<{
      data: { orderItemResponse: OrderItemResponse[] };
    }>(VersionURL("quotes"));

    return result.data.data.orderItemResponse.map((item) => ({
      id: item.id,
      jobNo: item.jobNo,
    }));
  }

  async AddProductToQuotedOrder(
    quoteId: string,
    item: CartItemRequest
  ): Promise<BaseResponse<ProductListResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<ProductListResponse>
    >(VersionURL(`quotes/${quoteId}`), item);

    return result.data;
  }

  async createNewEstimateOrTemplate(
    type: ProductListEnumType,
    name: string,
    description: string
  ): Promise<BaseResponse<ProductListResponse>> {
    const payload = {
      ProductListType: type,
      name: name,
      description: description,
    };

    const result = await axiosRESTService.post<
      BaseResponse<ProductListResponse>
    >(
      VersionURL(
        type == ProductListEnumType.QuoteList ? "estimates" : "templates"
      ),
      payload
    );

    return result.data;
  }

  async AddProductToEstimate({
    estimateId,
    source,
    products,
    cartItemIds,
    orderId,
    orderItemId,
  }: {
    estimateId: string;
    source: ItemSourceEnum;
    products?: CartItemRequest[];
    cartItemIds?: number[];
    orderId?: number;
    orderItemId?: number;
  }): Promise<BaseResponse<ProductListResponse>> {
    const payload = {
      Products: products,
      ItemSource: source,
      CartItemIds: cartItemIds,
      OrderId: orderId,
      OrderItemId: orderItemId,
    };

    const result = await axiosRESTService.post<
      BaseResponse<ProductListResponse>
    >(VersionURL(`estimates/${estimateId}/items`), payload);

    return result.data;
  }

  async AddProductToTemplates({
    templateId,
    source,
    products,
    cartItemIds,
    orderId,
    orderItemId,
  }: {
    templateId: string;
    source: ItemSourceEnum;
    products?: CartItemRequest[];
    cartItemIds?: number[];
    orderId?: number;
    orderItemId?: number;
  }): Promise<BaseResponse<ProductListResponse>> {
    const payload = {
      Products: products,
      ItemSource: source,
      CartItemIds: cartItemIds,
      OrderId: orderId,
      OrderItemId: orderItemId,
    };

    const result = await axiosRESTService.post<
      BaseResponse<ProductListResponse>
    >(VersionURL(`templates/${templateId}/items`), payload);

    return result.data;
  }

  getRenaissanceQuoteRequest(productId: number) {
    return axiosRESTService.get<BaseResponse<VirtualProductQuoteResponse>>(
      VersionURL(`products/${productId}/renaissance`)
    );
  }

  async sendRenaissanceQuoteRequest(
    productId: number,
    payload: VirtualProductQuoteRequest
  ) {
    return axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`products/${productId}/renaissance`),

      payload
    );
  }

  getCustomWindowsQuoteRequest(productId: number) {
    return axiosRESTService.get<BaseResponse<VirtualProductQuoteResponse>>(
      VersionURL(`products/${productId}/custom-windows`)
    );
  }

  async getCart(): Promise<BaseResponse<CartResponse>> {
    const result = await axiosRESTService.get<BaseResponse<CartResponse>>(
      VersionURL(`cart`)
    );
    return result.data;
  }

  async updateCartItemQuantity(itemId: number, quantity: number) {
    const result = await axiosRESTService.put<BaseResponse<CartResponse>>(
      VersionURL(`cart/items/${itemId}`),
      quantity,
      { headers: { "Content-Type": "application/json" } }
    );
    return result.data;
  }

  async addCartSpecificationLines(
    cartItemId: number,
    specificationLines: CartSpecificationLine[]
  ) {
    const result = await axiosRESTService.post(
      VersionURL(`cart/${cartItemId}/specification-lines`),
      specificationLines
    );
    return result.data;
  }

  async deleteCartSpecificationLine(specificationLineId: number) {
    const result = await axiosRESTService.delete<BaseResponse<boolean>>(
      VersionURL(`cart/specification-lines/${specificationLineId}`)
    );
    return result.data;
  }

  async updateRiserPan(cartItemId: number, riserPan: RiserPan) {
    const result = await axiosRESTService.post(
      VersionURL(`cart/${cartItemId}/riser-pan`),
      riserPan
    );
    return result.data;
  }

  async removeCartItem(itemId: number) {
    const result = await axiosRESTService.delete<BaseResponse<CartResponse>>(
      VersionURL(`cart/items/${itemId}`),
      {
        params: {
          resetCheckoutData: true,
          ensureOnlyActiveCheckoutAttributes: true,
        },
      }
    );
    return result.data;
  }

  async removeCartItems() {
    const result = await axiosRESTService.delete<BaseResponse<CartResponse>>(
      VersionURL(`cart/items`)
    );
    return result.data;
  }

  async applyPromoToCart(couponCode: string) {
    const result = await axiosRESTService.post<BaseResponse<any>>(
      VersionURL(`cart/promotions`),
      null,
      { params: { couponCode } }
    );
    return result.data;
  }

  async removePromoFromCart(couponCode: string) {
    const result = await axiosRESTService.delete<BaseResponse<any>>(
      VersionURL(`cart/promotions`),
      { params: { couponCode } }
    );
    return result.data;
  }

  async shareShoppingCart(request: ShareShoppingCartRequest) {
    const result = await axiosRESTService.post<
      BaseResponse<ShareShoppingCartRequest>
    >(VersionURL(`cart/action/share`), request);
    const data = result.data;
    if (!data?.success) {
      // Normalize error like other methods
      const err = new Error(data?.message ?? "Failed to share cart.");
      // throw to let UI show snackbar
      throw err;
    }

    return data;
  }

  async getOrderHistory(
    payload: OrderHistoryFilterPayload
  ): Promise<BaseResponse<OrderHistoryResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<OrderHistoryResponse>
    >(VersionURL(URLS.ORDERS_HISTORY), payload);

    return result.data;
  }

  async sendOrderChangeRequest(
    payload: SendOrderChangeRequestPayload,
    orderId: number
  ): Promise<BaseResponse<object>> {
    const result = await axiosRESTService.post<BaseResponse<object>>(
      VersionURL(`${URLS.ORDERS}/${orderId}`),
      payload
    );

    return result.data;
  }

  async orderHistoryStatusCount(
    payload: OrderHistoryStatusCountPayload
  ): Promise<BaseResponse<OrderHistoryStatusCountResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<OrderHistoryStatusCountResponse>
    >(VersionURL(URLS.ORDERS_HISTORY_STATUS_COUNT), payload);

    return result.data;
  }

  async sendQuickReturnRequest(payload: QuickReturnPayload): Promise<boolean> {
    const { orderId, note, palletFlag } = payload;

    const url =
      VersionURL(`/orders/quick-return/${orderId}`) +
      `&note=${encodeURIComponent(note)}&palletFlag=${palletFlag}`;

    const result = await axiosRESTService.post<boolean>(url, []);

    return result.data;
  }

  async reorder(orderId: number): Promise<EmptyBaseResponse> {
    const result = await axiosRESTService.post<EmptyBaseResponse>(
      `${VersionURL(URLS.CART_ITEMS)}&orderId=${orderId}`,
      []
    );

    return result.data;
  }

  async fetchLookupOptionsRaw(
    payload: fetchLookupOptionsPayload
  ): Promise<BaseResponse<ApiLookupResponse>> {
    const params: Record<string, string | number | string[]> = {
      lookupType: payload.lookupType,
      page: payload.page,
      pageSize: payload.pageSize,
    };

    if (payload.search?.trim()) {
      params.search = payload.search.trim();
    }

    if (payload.filter?.length) {
      params.filter = payload.filter;
    }

    const result = await axiosRESTService.get<BaseResponse<ApiLookupResponse>>(
      VersionURL(URLS.ORDERS_FILTER_LOOKUP),
      {
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, { arrayFormat: "repeat" }),
        signal: payload.signal,
      }
    );

    return result.data;
  }

  async getExportOrderDataForPdf(
    orderId: number
  ): Promise<BaseResponse<OrderDataForPDF>> {
    const result = await axiosRESTService.get<BaseResponse<OrderDataForPDF>>(
      `${VersionURL(`${URLS.ORDERS}/${URLS.REPORTS}/${orderId}`)}`
    );

    return result.data;
  }

  async getExportOrdersDataForPdf(
    filters: OrderHistoryFilterPayload
  ): Promise<BaseResponse<OrderDataForPDF[]>> {
    const result = await axiosRESTService.post<BaseResponse<OrderDataForPDF[]>>(
      `${VersionURL(`${URLS.ORDERS}/${URLS.REPORTS}`)}`,
      filters
    );

    return result.data;
  }

  async exportToExcelRaw(
    filters: OrderHistoryFilterPayload,
    exportType: ExportTypeEnum
  ): Promise<AxiosResponse<Blob>> {
    const url = `${VersionURL(
      `${URLS.ORDERS}/${URLS.REPORTS}/${URLS.EXCEL}`
    )}&exportType=${exportType}`;

    return axiosRESTService.post(url, filters, { responseType: "blob" });
  }

  async getOrderDetailsById(
    orderId: number
  ): Promise<BaseResponse<OrderDetailsResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<OrderDetailsResponse>
    >(VersionURL(`${URLS.ORDERS}/${orderId}`));

    return result.data;
  }

  async orderDeliveryScheduleById(
    payload: OrderDeliverySchedulePayload,
    orderId: number
  ): Promise<EmptyBaseResponse> {
    const url = VersionURL(`${URLS.ORDERS}/${orderId}/${URLS.DELIVERY}`);
    const params: Record<string, string> = {
      time: payload.time,
      date: payload.date,
    };

    if (payload.note.length > 0) {
      params.note = payload.note;
    }

    const response = await axiosRESTService.post<EmptyBaseResponse>(url, null, {
      params,
      paramsSerializer: (params) =>
        qs.stringify(params, { arrayFormat: "repeat" }),
    });

    return response.data;
  }

  async getDQDeliveryOrderById(
    orderId: number,
    excludeImages: boolean = false
  ): Promise<BaseResponse<DQDeliveryOrderResponse>> {
    const url = `${VersionURL(
      `${URLS.DQ}/${URLS.DELIVERY_ORDER}/${orderId}`
    )}&excludeImages=${excludeImages}`;

    const result = await axiosRESTService.get<
      BaseResponse<DQDeliveryOrderResponse>
    >(url);

    return result.data;
  }

  async sendCustomWindowsQuoteRequest(productId: number, formData: FormData) {
    return axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`products/${productId}/custom-windows`),
      formData
    );
  }

  async getHexToColorMapping(): Promise<BaseResponse<any>> {
    const result = await axiosService.get<BaseResponse<any>>(
      VersionURL(URLS.GET_HEX_TO_COLOR_MAPPING)
    );

    return result.data;
  }

  async getCheckout(
    request: CheckoutRequest
  ): Promise<BaseResponse<CheckoutResponse>> {
    const result = await axiosRESTService.get<BaseResponse<CheckoutResponse>>(
      VersionURL(`checkout`),
      {
        params: {
          request,
        },
      }
    );
    return result.data;
  }

  async getOrderCompleted(
    orderId: string
  ): Promise<BaseResponse<CheckoutCompletedResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<CheckoutCompletedResponse>
    >(VersionURL(`checkout/completed/${orderId}`));
    return result.data;
  }

  //Manage User

  async getManageUsers(): Promise<BaseResponse<ManageUsersResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<ManageUsersResponse>
    >(VersionURL(`account/users`));

    return result.data;
  }

  async addSecondaryUser(
    payload: SecondaryUserRequest
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/users`),
      payload
    );
    return result.data;
  }

  async updateSecondaryUser(
    payload: SecondaryUserRequest,
    userId: Number
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.patch<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}`),
      payload
    );
    return result.data;
  }

  async approvePendingUser(
    payload: ApproveUserRequest,
    userId: Number
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.patch<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}/approval`),
      payload
    );
    return result.data;
  }

  async removeSecondaryUser(userId: Number): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.delete<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}`)
    );
    return result.data;
  }

  async secondaryUserBlocked(userId: Number): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.patch<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}/activation`)
    );
    return result.data;
  }

  async getCustomerAccount(): Promise<BaseResponse<CustomerAccountResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<CustomerAccountResponse>
    >(VersionURL(`account/customer`));

    return result.data;
  }

  async updateCustomerAccount(
    payload: CustomerAccountRequest
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/customer`),
      payload
    );
    return result.data;
  }
  async getUserProfile(
    userId: Number
  ): Promise<BaseResponse<UserProfileResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<UserProfileResponse>
    >(VersionURL(`account/users/${userId}/profile`));

    return result.data;
  }
  async updateUserProfile(
    payload: UserProfileRequest,
    userId: Number
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}/profile`),
      payload
    );
    return result.data;
  }

  async updateUserProfilePassword(
    payload: UpdatePasswordRequest
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/auth/password-reset`),
      payload
    );
    return result.data;
  }

  async passwordReset(
    customerEmail: string,
    captchaToken: string
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/auth/password-recovery`),
      JSON.stringify(customerEmail),
      {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          captchaToken,
        },
      }
    );
    return result.data;
  }

  async getRecaptchaSiteKey(): Promise<string> {
    const res = await axiosRESTService.get<BaseResponse<string>>(
      VersionURL("account/auth/recaptcha-site-key")
    );

    if (!res.data?.success || !res.data.data) {
      throw new Error(res.data?.error || "Failed to load reCAPTCHA site key.");
    }

    return res.data.data;
  }

  async updateOrderNotifications(
    payload: OrderNotificationSettingRequest,
    userId: Number
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`account/users/${userId}/notifications`),
      payload
    );
    return result.data;
  }

  async getOrderNotification(
    userId: Number
  ): Promise<BaseResponse<OrderNotificationResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<OrderNotificationResponse>
    >(VersionURL(`account/users/${userId}/notifications`));

    return result.data;
  }

  async AddAdditionalOrderDeliverySubscriptions(
    payload: any
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL("checkout/delivery/subscription"),
      payload
    );
    return result.data;
  }

  async getAllCustomerShipTos(
    customerNumber: string | null
  ): Promise<BaseResponse<Lookup[]>> {
    const result = await axiosRESTService.get<BaseResponse<Lookup[]>>(
      VersionURL(`account/shipTos/lookup`),
      {
        params: {
          customerNumber: customerNumber,
        },
      }
    );

    return result.data;
  }

  async getUserSelectedShipTos(
    userId: number
  ): Promise<BaseResponse<UserSelectedShipToResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<UserSelectedShipToResponse>
    >(VersionURL(`account/users/${userId}/shipTos`), {});
    return result.data;
  }

  async validateCustomerShipToUpdate(id: number) {
    const result = await axiosRESTService.post<BaseResponse<ShipToChange>>(
      VersionURL(`checkout/shipTos/${id}/validate-branch-change`)
    );
    return result.data;
  }

  async validateSecondaryUserEmail(payload: ValidateEmailRequest) {
    const result = await axiosRESTService.post<
      BaseResponse<ValidateEmailResponse>
    >(VersionURL("account/validate-email"), payload);

    return result.data;
  }

  async updateCustomerShipTo(id: number) {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`checkout/shipTos/${id}`)
    );
    return result.data;
  }

  async placeOrder(payload: FormData, createQuote: boolean | null) {
    const result = await axiosRESTService.post<
      BaseResponse<PlaceOrderResponse>
    >(VersionURL(`checkout`), payload, {
      params: createQuote !== null ? { createQuote } : undefined,
    });
    return result.data;
  }

  async sendWindowsPatioDoorsSSE(
    productId: Number,
    payload: WindowsAndPatioDoorsActivityRequest
  ) {
    const result = await axiosRESTService.post<
      BaseResponse<PlaceOrderResponse>
    >(VersionURL(`activity-events/${productId}/windows-patio-doors`), payload);
    return result.data;
  }

  async sendColorMatchToolSSE(productId: Number) {
    const result = await axiosRESTService.post<
      BaseResponse<PlaceOrderResponse>
    >(VersionURL(`activity-events/${productId}/color-match-tool`));
    return result.data;
  }

  async sendDownloadDocumentSSE(productId: Number, fileName: string) {
    const result = await axiosRESTService.post<
      BaseResponse<PlaceOrderResponse>
    >(VersionURL(`activity-events/${productId}/download-document`), {
      params: {
        fileName: fileName,
      },
    });
    return result.data;
  }

  async sendProductDetailsPageSSE(productId: Number) {
    const result = await axiosRESTService.post<
      BaseResponse<PlaceOrderResponse>
    >(VersionURL(`activity-events/${productId}/product-details-page`));
    return result.data;
  }

  async getFeaturedCategories(
    warehouseCode: string | null
  ): Promise<BaseResponse<string[]>> {
    const result = await axiosService.get<BaseResponse<string[]>>(
      VersionURL(URLS.GET_FEATURED_CATEGORIES),
      {
        params: {
          warehouseCode: warehouseCode,
        },
      }
    );

    return result.data;
  }

  async getShipTos(searchTerm: string | null): Promise<BaseResponse<ShipTo[]>> {
    const result = await axiosService.get<BaseResponse<ShipTo[]>>(
      VersionURL(URLS.GET_SHIP_TOS),
      {
        params: {
          searchTerm: searchTerm,
        },
      }
    );

    return result.data;
  }

  async validateShipToChange(
    newShipToId: number
  ): Promise<BaseResponse<ShipToChange>> {
    const result = await axiosService.get<BaseResponse<ShipToChange>>(
      VersionURL(URLS.VALIDATE_SHIP_TO_CHANGE),
      {
        params: {
          newShipToId: newShipToId,
        },
      }
    );

    return result.data;
  }

  async setSelectedShipTo(
    newShipToId: string | null
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosService.post<BaseResponse<boolean>>(
      VersionURL(URLS.SET_SELECTED_SHIP_TO),
      null,
      {
        params: {
          newShipToId: newShipToId,
        },
      }
    );

    return result.data;
  }

  async getSecondaryUserInfo(): Promise<BaseResponse<CustomerUser>> {
    const result = await axiosService.get<BaseResponse<CustomerUser>>(
      VersionURL(URLS.GET_SECONDARY_USER_INFO)
    );

    return result.data;
  }

  async getMiniShoppingCart(): Promise<BaseResponse<MiniShoppingCartModel>> {
    const result = await axiosService.get<BaseResponse<MiniShoppingCartModel>>(
      VersionURL(URLS.GET_MINI_SHOPPING_CART)
    );

    return result.data;
  }

  async updateMiniCartItemQty(
    shoppingCartItemId: number,
    quantity: number
  ): Promise<BaseResponse<MiniShoppingCartModel>> {
    const result = await axiosService.post<BaseResponse<MiniShoppingCartModel>>(
      VersionURL(URLS.UPDATE_MINI_CART_ITEM_QUANTITY),
      null,
      {
        params: {
          shoppingCartItemId: shoppingCartItemId,
          quantity: quantity,
        },
      }
    );

    return result.data;
  }

  async deleteMiniCartItem(
    shoppingCartItemId: number
  ): Promise<BaseResponse<MiniShoppingCartModel>> {
    const result = await axiosService.post<BaseResponse<MiniShoppingCartModel>>(
      VersionURL(URLS.DELETE_MINI_CART_ITEM),
      null,
      {
        params: {
          shoppingCartItemId: shoppingCartItemId,
        },
      }
    );

    return result.data;
  }

  async applyPromo(couponCode: string): Promise<BaseResponse<string>> {
    const result = await axiosRESTService.post<BaseResponse<string>>(
      VersionURL("/cart/promotions"),
      null,
      { params: { couponCode } }
    );

    return result.data;
  }

  async removePromo(couponCode: string): Promise<BaseResponse<string>> {
    const result = await axiosRESTService.delete<BaseResponse<string>>(
      VersionURL("/cart/promotions"),
      { params: { couponCode } }
    );

    return result.data;
  }

  async getFeaturedCategoriesVisibilities(
    warehouseCode: string,
    isAuthenticated: boolean
  ): Promise<BaseResponse<{ [key: string]: FeaturedCategory }>> {
    const result = await axiosService.get<
      BaseResponse<{ [key: string]: FeaturedCategory }>
    >(VersionURL(URLS.GET_FEATURED_CATEGORIES_VISIBILITIES), {
      params: {
        warehouseCode: warehouseCode,
        isAuthenticated: isAuthenticated,
      },
    });

    return result.data;
  }

  async getBranchDetails(
    warehouseDisplayName: string | null,
    fullStateName: string | null,
    isAuthenticated: boolean,
    clientUtcOffsetInMinutes: number,
    userId: number | null
  ): Promise<BaseResponse<BranchPageInfo>> {
    const result = await axiosService.get<BaseResponse<any>>(
      VersionURL(URLS.GET_BRANCH_DETAILS),
      {
        params: {
          warehouseDisplayName: warehouseDisplayName,
          fullStateName: fullStateName,
          isAuthenticated: isAuthenticated,
          clientUtcOffsetInMinutes: clientUtcOffsetInMinutes,
          userId: userId,
        },
      }
    );

    return result.data;
  }

  async getStateBranches(
    stateFullName: string,
    clientUtcOffsetInMinutes: number
  ): Promise<BaseResponse<StateBranches>> {
    const result = await axiosService.get<BaseResponse<StateBranches>>(
      VersionURL(URLS.GET_STATE_BRANCHES),
      {
        params: {
          stateFullName: stateFullName,
          clientUtcOffsetInMinutes: clientUtcOffsetInMinutes,
        },
      }
    );

    return result.data;
  }

  async getOverallBranches(): Promise<BaseResponse<OverallBranches>> {
    const result = await axiosService.get<BaseResponse<OverallBranches>>(
      VersionURL(URLS.GET_OVERALL_BRANCHES)
    );

    return result.data;
  }

  async getdQDashboard(): Promise<BaseResponse<DQDashboardResponse>> {
    const result = await axiosRESTService.get<
      BaseResponse<DQDashboardResponse>
    >(VersionURL("account/dqdashboard"));

    return result.data;
  }

  async getFilteredDQDashboard(
    request: DQDashboardRequest
  ): Promise<BaseResponse<DQDashboardResponseBase>> {
    const result = await axiosRESTService.post<
      BaseResponse<DQDashboardResponseBase>
    >(VersionURL("account/dqdashboard"), request);
    return result.data;
  }
  async getGASettings(): Promise<BaseResponse<GoogleAnalyticsSettings>> {
    const result = await axiosRESTService.get<
      BaseResponse<GoogleAnalyticsSettings>
    >(VersionURL(URLS.GET_GOOGLE_Analytics_SETTINGS));

    return result.data;
  }

  async getContactUs(
    warehouseCode: string | null
  ): Promise<BaseResponse<ContactUsDto>> {
    const result = await axiosRESTService.get<BaseResponse<ContactUsDto>>(
      VersionURL("contact/contact"),
      {
        params: {
          warehouseCode: warehouseCode ?? undefined,
        },
      }
    );
    return result.data;
  }

  async sendContactUs(payload: ContactUsDto): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL("contact/contact"),
      payload
    );
    return result.data;
  }

  async downloadAttachmentRaw(
    attachmentId: number
  ): Promise<AxiosResponse<Blob>> {
    const url = VersionURL(
      `${URLS.ORDERS}/${URLS.REPORTS}/${URLS.ATTACHMENT}/${attachmentId}`
    );

    return axiosRESTService.get(url, { responseType: "blob" });
  }

  async downloadOrderAttachmentsRaw(
    orderId: number
  ): Promise<AxiosResponse<Blob>> {
    const url = VersionURL(
      `${URLS.ORDERS}/${URLS.REPORTS}/${URLS.ATTACHMENTS}/${orderId}`
    );

    return axiosRESTService.get(url, { responseType: "blob" });
  }

  async downloadEagleViewDocumentRaw(
    orderId: number
  ): Promise<AxiosResponse<Blob>> {
    const url = VersionURL(
      `${URLS.ORDERS}/${URLS.REPORTS}/${URLS.EAGLEVIEW_DOCUMENT}/${orderId}`
    );

    return axiosRESTService.get(url, { responseType: "blob" });
  }

  async getMarketingContactUs(): Promise<BaseResponse<MarketingContactUsDto>> {
    const result = await axiosRESTService.get<
      BaseResponse<MarketingContactUsDto>
    >(VersionURL("contact/marketing"));
    return result.data;
  }

  async sendMarketingContactUs(
    formData: FormData
  ): Promise<BaseResponse<boolean>> {
    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL("contact/marketing"),
      formData
    );

    return result.data;
  }

  async fetchReturnOrderItems(
    orderId: string,
    payload: ReturnOrderRequest
  ): Promise<BaseResponse<ReturnOrderResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<ReturnOrderResponse>
    >(VersionURL(`orders/${orderId}/return-items`), payload);
    return result.data;
  }

  async uploadReturnOrderItemImage(
    orderId: number,
    orderItemId: number,
    file: File
  ): Promise<BaseResponse<boolean>> {
    const formData = new FormData();
    formData.append("file", file);

    const result = await axiosRESTService.post<BaseResponse<boolean>>(
      VersionURL(`orders/${orderId}/return-items/${orderItemId}/upload`),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return result.data;
  }

  async changeReturnItem(
    orderId: number,
    orderItemId: number,
    request: {
      quantity: number;
      note: string;
      palletFlag: boolean;
    }
  ) {
    try {
      const response = await axiosRESTService.post(
        `orders/${orderId}/return-items/${orderItemId}`,
        request
      );
      return response.data;
    } catch (error) {
      console.error("Error updating return order item", error);
      throw error;
    }
  }

  async changeReturnItemSelection(
    orderId: number,
    selected: boolean,
    orderItemId: string | null
  ): Promise<BaseResponse<boolean>> {
    const payload = {
      orderItemId,
      selected,
    };
    const result = await axiosRESTService.patch<BaseResponse<boolean>>(
      VersionURL(`orders/${orderId}/return-items/selection`),
      payload
    );

    return result.data;
  }

  async fetchReturnOrderItemsDetails(
    orderId: string
  ): Promise<BaseResponse<ReturnItemsDetailsResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<ReturnItemsDetailsResponse>
    >(VersionURL(`orders/${orderId}/return-items-details`));
    return result.data;
  }

  async sendOrderAdvanceReturn(
    orderId: string,
    payload: OrderAdvanceReturnRequest
  ): Promise<BaseResponse<OrderAdvanceReturnResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<OrderAdvanceReturnResponse>
    >(VersionURL(`orders/${orderId}/advance-return`), payload);
    return result.data;
  }

  async getContractorData(): Promise<BaseResponse<ContractorData[]>> {
    const result = await axiosRESTService.post<BaseResponse<ContractorData[]>>(
      VersionURL(`${URLS.LOYALTY}/enrollment`)
    );

    return result.data;
  }

  async updateContractorData(
    payload: ContractorData
  ): Promise<EmptyBaseResponse> {
    const result = await axiosRESTService.post<EmptyBaseResponse>(
      VersionURL(`${URLS.LOYALTY}/enrollment/update`),
      payload
    );

    return result.data;
  }

  async getSubmissionHistory(
    payload: LoyaltyRebatePayload
  ): Promise<BaseResponse<LoyaltyRebateItemResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<LoyaltyRebateItemResponse>
    >(VersionURL(`${URLS.LOYALTY}/rebate`), payload);

    return result.data;
  }

  async getVendors(): Promise<BaseResponse<LoyaltyVendorResponse[]>> {
    const result = await axiosRESTService.get<
      BaseResponse<LoyaltyVendorResponse[]>
    >(VersionURL(`${URLS.LOYALTY}/vendors`));

    return result.data;
  }

  async getRebateDownloadData(
    vendor: string,
    monthYear: string
  ): Promise<BaseResponse<RebateInvoiceItem[]>> {
    const result = await axiosRESTService.get<
      BaseResponse<RebateInvoiceItem[]>
    >(
      VersionURL(
        `${URLS.LOYALTY}/rebate/download/rebateList/${vendor}/${monthYear}`
      )
    );

    return result.data;
  }

  async getRebateInvoiceData(
    vendor: string,
    monthYear: string
  ): Promise<BaseResponse<LoyaltyRebateInvoiceResponse[]>> {
    const result = await axiosRESTService.get<
      BaseResponse<LoyaltyRebateInvoiceResponse[]>
    >(
      VersionURL(
        `${URLS.LOYALTY}/rebate/download/invoice/${vendor}/${monthYear}`
      )
    );

    return result.data;
  }

  async getRewardsBalanceTotal(): Promise<BaseResponse<number>> {
    const result = await axiosRESTService.get<BaseResponse<number>>(
      VersionURL("rewards/balance/total")
    );

    return result.data;
  }

  async rewardsEnrollment(): Promise<BaseResponse<RewardsEnrollmentResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<RewardsEnrollmentResponse>
    >(VersionURL("rewards/enroll"));

    return result.data;
  }

  async rewardsEnrollmentStatus(): Promise<
    BaseResponse<RewardsEnrollmentStatusResponse>
  > {
    const result = await axiosRESTService.post<
      BaseResponse<RewardsEnrollmentStatusResponse>
    >(VersionURL("rewards/enrollment"));

    return result.data;
  }

  async getRewardsBalanceHistory(
    payload: RewardsBalanceHistoryPayload
  ): Promise<BaseResponse<RewardsBalanceHistoryResponse>> {
    const result = await axiosRESTService.post<
      BaseResponse<RewardsBalanceHistoryResponse>
    >(VersionURL("rewards/balance/history"), payload);

    return result.data;
  }

  async addRewards(email: string, phone: string): Promise<EmptyBaseResponse> {
    const result = await axiosRESTService.get<EmptyBaseResponse>(
      VersionURL(`rewards/add`),
      {
        params: {
          email: email,
          phone: phone,
        },
      }
    );

    return result.data;
  }
}

const dataService = new DataService();
export default dataService;
